/**
 * Tenant-scoped React Query cache helpers.
 *
 * Tenant identity reaches the API only via the `X-Retailer-ID` / `X-Environment`
 * request headers (see `api/tRpc.ts`), NOT as part of the query key. As a result,
 * switching retailer leaves the previous tenant's data cached under the same key.
 * `resetTenantScopedQueries` evicts that cache on a switch so active queries refetch
 * with the new headers instead of serving stale data until the next poll.
 * @packageDocumentation
 */

import type { QueryKey, QueryClient } from '@tanstack/react-query';

/**
 * tRPC queries (`router.procedure`) whose data is scoped to the authenticated user — not the
 * selected tenant — and so must survive a retailer switch. Only the queries the UI actually issues
 * are listed; `retailers.list` reads the LDA session, never the `X-Retailer-ID` /
 * `X-Environment` headers, so its cached data stays valid across a switch (evicting it would
 * needlessly reload the environment selector). Everything else is tenant-scoped and refetched on
 * switch — the safe default, so cross-tenant data is never served.
 *
 * Matching per-procedure (not per-router) keeps that default safe: a NEW procedure — even one under
 * `retailers`/`environments` — is tenant-scoped until listed here. Add one ONLY if it is
 * user/session-scoped and the UI queries it; a tenant-scoped procedure must be left off.
 */
const TENANT_INDEPENDENT_QUERIES = new Set<string>(['retailers.list']);

/**
 * Determines whether a query key is tenant-independent and must survive a retailer switch.
 *
 * tRPC React Query keys are shaped `[pathSegments[], { input?, type }]`, so joining the first
 * element yields the `router.procedure` identifier matched against the allowlist. Non-tRPC keys
 * (whose first element is not an array) are treated as tenant-scoped.
 *
 * @param { QueryKey } queryKey The React Query key to inspect.
 * @returns { boolean } Returns true if the query is tenant-independent.
 */
export const isTenantIndependentQueryKey = (queryKey: QueryKey): boolean => {
  const path = queryKey[0];

  return Array.isArray(path) && TENANT_INDEPENDENT_QUERIES.has(path.join('.'));
};

/**
 * Evicts all tenant-scoped React Query cache on a retailer/environment switch.
 *
 * Cancels in-flight (previous-tenant) requests first so the subsequent refetch is not
 * deduped onto them, then resets the tenant-scoped queries — this clears their data and
 * refetches active observers with the new tenant headers. Tenant-independent queries (the
 * retailer/environment lists) are preserved so the selector never blanks. Clearing the data
 * also lets each page's existing loading state render during the switch.
 *
 * @param { QueryClient } queryClient The TanStack Query client.
 * @returns { Promise<void> } Resolves once cancellation and reset have been dispatched.
 */
export const resetTenantScopedQueries = async (queryClient: QueryClient): Promise<void> => {
  const predicate = (query: { queryKey: QueryKey }): boolean => !isTenantIndependentQueryKey(query.queryKey);

  await queryClient.cancelQueries({ predicate });
  await queryClient.resetQueries({ predicate });
};
